import { useState, useEffect } from 'react';
import { db } from '@/lib/firebase';
import { useTranslation } from 'react-i18next';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { 
  Instagram, 
  Facebook, 
  MessageCircle, 
  Video, 
  Save, 
  Loader2, 
  Globe,
  MapPin,
  Phone
} from 'lucide-react';

export default function BusinessInfoPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [info, setInfo] = useState({
    instagram: '',
    facebook: '',
    tiktok: '',
    whatsapp: '',
    address: '',
    phone: '',
  });

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'business_info'), (docSnap) => {
      if (docSnap.exists()) {
        setInfo(docSnap.data() as any);
      }
      setLoading(false);
    });

    return () => unsub();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSuccess(false);

    try {
      await setDoc(doc(db, 'settings', 'business_info'), {
        ...info,
        updatedAt: new Date().toISOString()
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error("Error saving business info:", error);
      alert("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#F57F25]" size={48} />
      </div>
    );
  }

  return (
    <div className="max-w-5xl space-y-10">
      <div>
        <h1 className="text-3xl font-black text-white uppercase tracking-tight">{t('admin_business_title')}</h1>
        <p className="text-white/50 text-sm">{t('admin_business_sub')}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-10">
        
        {/* Physical Contact Section */}
        <section className="space-y-6">
          <h2 className="text-xs font-black text-[#F57F25] uppercase tracking-[0.2em] border-b border-white/5 pb-2">{t('admin_business_location')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-[#111] p-8 rounded-[32px] border border-white/5 space-y-4">
              <div className="flex items-center gap-3 text-white mb-2">
                <MapPin size={24} className="text-[#F57F25]" />
                <h3 className="font-black uppercase tracking-widest text-xs">{t('admin_business_address')}</h3>
              </div>
              <input
                type="text"
                value={info.address}
                onChange={e => setInfo({ ...info, address: e.target.value })}
                className="w-full bg-black border border-white/10 rounded-2xl py-4 px-6 text-white focus:border-[#F57F25] outline-none transition-all text-sm"
                placeholder={t('admin_business_address_placeholder')}
              />
            </div>

            <div className="bg-[#111] p-8 rounded-[32px] border border-white/5 space-y-4">
              <div className="flex items-center gap-3 text-white mb-2">
                <Phone size={24} className="text-[#F57F25]" />
                <h3 className="font-black uppercase tracking-widest text-xs">{t('admin_business_phone')}</h3>
              </div>
              <input
                type="text"
                value={info.phone}
                onChange={e => setInfo({ ...info, phone: e.target.value })}
                className="w-full bg-black border border-white/10 rounded-2xl py-4 px-6 text-white focus:border-[#F57F25] outline-none transition-all text-sm"
                placeholder={t('admin_business_phone_placeholder')}
              />
            </div>
          </div>
        </section>

        {/* Social Media Section */}
        <section className="space-y-6">
          <h2 className="text-xs font-black text-[#F57F25] uppercase tracking-[0.2em] border-b border-white/5 pb-2">{t('admin_business_social')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-[#111] p-8 rounded-[32px] border border-white/5 space-y-4">
              <div className="flex items-center gap-3 text-white mb-2">
                <Instagram size={24} className="text-[#F57F25]" />
                <h3 className="font-black uppercase tracking-widest text-xs">Instagram</h3>
              </div>
              <input
                type="url"
                value={info.instagram}
                onChange={e => setInfo({ ...info, instagram: e.target.value })}
                className="w-full bg-black border border-white/10 rounded-2xl py-4 px-6 text-white focus:border-[#F57F25] outline-none transition-all text-sm"
                placeholder="https://instagram.com/bucaraburger"
              />
            </div>

            <div className="bg-[#111] p-8 rounded-[32px] border border-white/5 space-y-4">
              <div className="flex items-center gap-3 text-white mb-2">
                <Facebook size={24} className="text-[#F57F25]" />
                <h3 className="font-black uppercase tracking-widest text-xs">Facebook</h3>
              </div>
              <input
                type="url"
                value={info.facebook}
                onChange={e => setInfo({ ...info, facebook: e.target.value })}
                className="w-full bg-black border border-white/10 rounded-2xl py-4 px-6 text-white focus:border-[#F57F25] outline-none transition-all text-sm"
                placeholder="https://facebook.com/bucaraburger"
              />
            </div>

            <div className="bg-[#111] p-8 rounded-[32px] border border-white/5 space-y-4">
              <div className="flex items-center gap-3 text-white mb-2">
                <Video size={24} className="text-[#F57F25]" />
                <h3 className="font-black uppercase tracking-widest text-xs">TikTok</h3>
              </div>
              <input
                type="url"
                value={info.tiktok}
                onChange={e => setInfo({ ...info, tiktok: e.target.value })}
                className="w-full bg-black border border-white/10 rounded-2xl py-4 px-6 text-white focus:border-[#F57F25] outline-none transition-all text-sm"
                placeholder="https://tiktok.com/@bucaraburger"
              />
            </div>

            <div className="bg-[#111] p-8 rounded-[32px] border border-white/5 space-y-4">
              <div className="flex items-center gap-3 text-white mb-2">
                <MessageCircle size={24} className="text-[#F57F25]" />
                <h3 className="font-black uppercase tracking-widest text-xs">{t('admin_business_whatsapp')}</h3>
              </div>
              <input
                type="text"
                value={info.whatsapp}
                onChange={e => setInfo({ ...info, whatsapp: e.target.value })}
                className="w-full bg-black border border-white/10 rounded-2xl py-4 px-6 text-white focus:border-[#F57F25] outline-none transition-all text-sm"
                placeholder={t('admin_business_whatsapp_placeholder')}
              />
            </div>
          </div>
        </section>

        <div className="flex items-center gap-6 pt-4">
          <button
            type="submit"
            disabled={saving}
            className="bg-[#F57F25] hover:bg-[#e06d15] text-black font-black px-10 py-5 rounded-3xl transition-all flex items-center justify-center gap-3 uppercase text-sm shadow-xl shadow-[#F57F25]/20 disabled:opacity-50"
          >
            {saving ? <Loader2 className="animate-spin" size={20} /> : <><Save size={20} /> {t('admin_business_update')}</>}
          </button>
          
          {success && (
            <div className="flex items-center gap-2 text-green-500 font-bold animate-bounce">
              <Globe size={20} />
              <span className="text-xs uppercase tracking-widest">{t('admin_business_success')}</span>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
